import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import Img from '../../Img';
import Utr from '../../text/Utr';
import useClickOutsideRef from '../../hooks/useClickOutsideRef';
import { drawPositionTypeIds } from '../../../utils/eventConstants';
import { isPlayerCheckedIntoDraw } from '../../../utils/events';

import styles from './playerSelect.scss';

const TeamSelectV2 = (props) => {
    const {
        drawFormatType,
        match,
        playerOptions,
        playersDictByMemberId,
        teamsDict,
        utrType,
        isReadOnly,
        position,
        PTTPosition,
        PTTError,
        doNotMask = true,
        sportTypeId,
        drawId,
        'data-testid': dataTestId,
    } = props;

    const [filterString, setFilterString] = useState('');
    const [expand, setExpand] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    useClickOutsideRef(wrapperRef, () => setExpand(false));

    const handleExpand = () => {
        setFilterString('');
        setExpand(true);
        setTimeout(inputRef.current?.focus(), 10);
    };

    const handleCollapse = () => {
        setExpand(false);
    };

    const handleChange = (e) => {
        setFilterString(e.target.value);
    };

    const handleKeyUp = (e) => {
        if (e.key === 'Escape') {
            handleCollapse();
        }
    };

    const handleExpandClick = () => {
        if (!isReadOnly) {
            handleExpand();
        }
    };

    const handleClearClick = () => {
        handleCollapse();
        props.onSelect(position, null);
    };

    const handleByeClick = () => {
        handleCollapse();
        props.onSelect(position, { typeId: drawPositionTypeIds.BYE });
    };

    const shortNameById = (memberId) => {
        const player = playersDictByMemberId[memberId];
        return player ? `${player.firstName[0]}. ${player.lastName}` : '';
    };

    const utrById = (memberId) => {
        const player = playersDictByMemberId[memberId];
        return <Utr sportTypeId={sportTypeId} player={player} pbrClass={styles.pbr} force={{ ratingType: drawFormatType.value, utrType, showDecimals: true, doNotMask }} />;
    };

    let name;
    let teamMembers;
    const matchPosition = match[position];
    if (matchPosition?.teamId && teamsDict[matchPosition.teamId]?.members?.length) {
        // eslint-disable-next-line prefer-destructuring
        teamMembers = teamsDict[matchPosition.teamId].members;
        const seed = match[`${position}Seed`];
        const checkedInDrawPlayer1 = isPlayerCheckedIntoDraw(playersDictByMemberId[teamMembers[0]], drawId);
        const checkedInDrawPlayer2 = isPlayerCheckedIntoDraw(playersDictByMemberId[teamMembers[1]], drawId);
        name = (
            <span className="selected-name d-flex aic">
                <div className="d-flex aic">
                    {seed && <small className="selected-seed">{seed}</small>}
                    <span className="ellipsis">{shortNameById(teamMembers[0])}</span>
                    <small>{utrById(teamMembers[0])}</small>
                    {checkedInDrawPlayer1 && <span className="utr-icon icon-check-mark-green ml4 checkin-mark no-align" title="Player checked in" />}
                </div>
                <span className="d-block ml4 mr4">/</span>
                <div className="d-flex aic">
                    <span className="ellipsis">{shortNameById(teamMembers[1])}</span>
                    <small>{utrById(teamMembers[1])}</small>
                    {checkedInDrawPlayer2 && <span className="utr-icon icon-check-mark-green ml4 checkin-mark no-align" title="Player checked in" />}
                </div>
            </span>
        );
    } else if (matchPosition?.typeId === drawPositionTypeIds.BYE) {
        name = 'Bye';
    } else {
        name = 'Select a team';
    }

    let selectClass;
    if (!matchPosition?.teamId) {
        if (matchPosition?.typeId === drawPositionTypeIds.BYE) {
            selectClass = 'bye';
        } else {
            selectClass = 'empty';
        }
    } else if (matchPosition?.typeId === drawPositionTypeIds.BYE) {
        selectClass = 'bye';
    }

    return (
        <div className={`card-player-select`} ref={wrapperRef}>
            <div className="input-wrapper">
                <div
                    className={`player-select ${expand ? 'd-none' : 'd-flex aic jsb'} ${selectClass} ${PTTError ? 'text-danger' : ''} ${position === 'position2' ? 'mb2' : ''}`}
                    onClick={handleExpandClick}
                    data-testid={dataTestId}
                >
                    {name} {!isReadOnly && <i className="uts uts-expand" />}
                </div>
                {expand && (
                    <>
                        <input
                            ref={inputRef}
                            className={`player-select`}
                            value={filterString || ''}
                            onChange={handleChange}
                            onKeyUp={handleKeyUp}
                            placeholder="Type to filter players..."
                            data-testid={dataTestId && `${dataTestId}.filter-input`}
                        />
                        <i className={`uts uts-collapse`} onClick={handleCollapse} />
                    </>
                )}
                {!!PTTPosition && <div className="ptt-position">{PTTPosition}</div>}
            </div>
            {expand && (
                <div className={`select-options`}>
                    <ul>
                        {playerOptions?.length > 0 && playerOptions.map(team => {
                            const regex = new RegExp(filterString, 'i');
                            const checkedInDrawPlayer1 = isPlayerCheckedIntoDraw(playersDictByMemberId[team.members[0]], drawId);
                            const checkedInDrawPlayer2 = isPlayerCheckedIntoDraw(playersDictByMemberId[team.members[1]], drawId);
                            return (filterString && !team.displayName.match(regex)) ? null
                                : (
                                    <li key={team.id} onClick={() => { handleCollapse(); props.onSelect(position, team); }}>
                                        <div className={`row player-search-option`}>
                                            <div className="col-2 d-flex justify-content-center">
                                                <Img
                                                    src={'/img/v2/icons/icon-avatar-player.svg'}
                                                />
                                            </div>
                                            <div className="col-10">
                                                <div className="name">{shortNameById(team.members[0])} <small>{utrById(team.members[0])}</small> {checkedInDrawPlayer1 && <span className="utr-icon icon-check-mark-green checkin-mark" title="Player checked in" />}/ {shortNameById(team.members[1])} <small>{utrById(team.members[1])}</small> {checkedInDrawPlayer2 && <span className="utr-icon icon-check-mark-green checkin-mark" title="Player checked in" />}</div>
                                                <div className="place">Team</div>
                                            </div>
                                        </div>
                                    </li>
                                );
                        })}
                        {(!playerOptions || playerOptions.length === 0) &&
                            <li className="mt16">No teams to add...</li>}
                    </ul>
                    <div className="footer bt">
                        <div className="row no-gutters">
                            <div className="col-6">
                                <div className="footer-button" onClick={handleClearClick} data-testid={dataTestId && `${dataTestId}.clear-btn`}>
                                    <i className="uts uts-clear-player" /> Clear
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="footer-button bl" onClick={handleByeClick} data-testid={dataTestId && `${dataTestId}.bye-btn`}>
                                    <i className="uts uts-player-bye" /> Bye
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

TeamSelectV2.propTypes = {
    drawId: PropTypes.string,
    'data-testid': PropTypes.string,
};

TeamSelectV2.defaultProps = {
    drawId: '',
    'data-testid': null,
};

export default TeamSelectV2;
